import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, Shield } from 'lucide-react';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import api from '../utils/api';
import { getAuth } from '../utils/auth';

export default function RegisterAdmin() {
  const navigate = useNavigate();
  const { user } = getAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Only allow owner to access this page
    if (!user) {
      toast.error('Please login to access this page');
      navigate('/login');
      return;
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await api.post('/auth/register-admin', {
        email: formData.email,
        password: formData.password,
        role: 'admin'
      });
      toast.success('Admin account created successfully!');
      setFormData({ email: '', password: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create admin account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen noise-bg">
      <Navbar />
      
      <div className="pt-32 pb-12 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#00E055]/10 border border-[#00E055]/20 rounded-sm text-[#00E055] text-xs font-bold tracking-widest uppercase mb-6">
              <Shield className="w-4 h-4" />
              OWNER ONLY
            </div>
            <h1 className="text-4xl md:text-5xl font-black tracking-tighter uppercase mb-4" style={{fontFamily: 'Unbounded, sans-serif'}} data-testid="page-title">
              CREATE <span className="text-[#00E055]">ADMIN</span> ACCOUNT
            </h1>
            <p className="text-muted-foreground">Only the platform owner can create admin accounts</p>
          </div>

          <div className="bg-[#0A0A0A] border border-border rounded-lg p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email">Admin Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="admin@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="pl-10 bg-muted border-border focus:border-[#00E055] focus:ring-1 focus:ring-[#00E055]"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="pl-10 bg-muted border-border focus:border-[#00E055] focus:ring-1 focus:ring-[#00E055]"
                    required
                    data-testid="password-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    className="pl-10 bg-muted border-border focus:border-[#00E055] focus:ring-1 focus:ring-[#00E055]"
                    required
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-[#00E055] hover:bg-[#00E055]/90 text-black font-bold tracking-wide uppercase glow-primary"
                disabled={loading}
                data-testid="submit-button"
              >
                {loading ? 'CREATING ADMIN...' : 'CREATE ADMIN ACCOUNT'}
              </Button>
            </form>

            <div className="mt-6 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
              <p className="text-sm text-yellow-200">
                <strong>Important:</strong> Admin accounts have full control over the platform including approving/rejecting servers and managing all content.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
